import React, { useState, useEffect } from 'react';
import {
  FaSearch,
  FaCalendarAlt,
  FaUserTie,
  FaUsers,
  FaChartLine,
  FaClock,
  FaUserCheck,
  FaArrowRight,
  FaShare,
  FaBookmark,
  FaEye,
  FaTag,
  FaChevronRight,
  FaFilter,
  FaRocket,
  FaIdCard,
  FaTasks,
  FaGraduationCap,
  FaHandshake,
  FaBalanceScale,
  FaAward,
  FaComments,
  FaChartBar,
  FaTimes
} from 'react-icons/fa';
import './Blog.css';

const Blog = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [featuredPost, setFeaturedPost] = useState(0);
  const [sortBy, setSortBy] = useState('newest');
  const [selectedPost, setSelectedPost] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);

  // Blog categories
  const categories = [
    { id: 'all', name: 'All Articles', icon: <FaRocket />, count: 28 },
    { id: 'hiring', name: 'Hiring & Recruitment', icon: <FaUserTie />, count: 8 },
    { id: 'onboarding', name: 'Employee Onboarding', icon: <FaUserCheck />, count: 6 },
    { id: 'management', name: 'Employee Management', icon: <FaUsers />, count: 7 },
    { id: 'leadership', name: 'Manager Leadership', icon: <FaChartLine />, count: 5 },
    { id: 'analytics', name: 'HR Analytics', icon: <FaChartBar />, count: 2 }
  ];

  // All available tags
  const allTags = ['AI', 'Recruitment', 'Onboarding', 'Management', 'Leadership', 'Analytics', 'Compliance', 'Automation', 'HR Tech', 'Training', 'Performance', 'Retention'];

  // Featured blog posts (carousel)
  const featuredPosts = [
    {
      id: 1,
      title: 'The Future of AI in HR: Transforming Talent Acquisition',
      excerpt: 'Discover how artificial intelligence is revolutionizing the hiring process and what it means for your organization.',
      category: 'hiring',
      readTime: '8 min read',
      author: 'Sarah Chen',
      date: 'Dec 15, 2024',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&h=600&q=80',
      views: '2.4k',
      tags: ['AI', 'Recruitment', 'Innovation'],
      content: `Artificial Intelligence is transforming HR in unprecedented ways. In this comprehensive guide, we explore:

## Key AI Applications in HR:
• Automated resume screening and candidate matching
• Predictive analytics for employee retention
• AI-powered chatbots for employee queries
• Bias detection in hiring processes
• Skills gap analysis and training recommendations

## Benefits of AI in HR:
- 75% faster hiring processes
- 40% reduction in hiring bias
- Improved candidate experience
- Data-driven decision making

## Implementation Strategy:
1. Start with pilot programs
2. Train HR teams on AI tools
3. Ensure data privacy compliance
4. Measure ROI regularly

The future of HR is intelligent, automated, and data-driven.`
    },
    {
      id: 2,
      title: 'Creating Exceptional Onboarding Experiences in 2024',
      excerpt: 'Learn the key strategies for building onboarding programs that boost retention and employee satisfaction.',
      category: 'onboarding',
      readTime: '6 min read',
      author: 'Michael Rodriguez',
      date: 'Dec 12, 2024',
      image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&h=600&q=80',
      views: '1.8k',
      tags: ['Onboarding', 'Employee Experience', 'Retention'],
      content: `Exceptional onboarding is crucial for employee retention and productivity. Here's how to create memorable onboarding experiences:

## Onboarding Timeline:
**Pre-Day 1:**
- Send welcome package
- Set up equipment and access
- Assign a buddy/mentor

**First Week:**
- Company culture introduction
- Role-specific training
- Team introductions and social events

**First 90 Days:**
- Regular check-ins and feedback
- Goal setting and expectations
- Performance milestones

## Best Practices:
• Personalize the experience
• Use technology for automation
• Gather continuous feedback
• Measure onboarding success metrics

Companies with strong onboarding improve new hire retention by 82%.`
    },
    {
      id: 3,
      title: 'Modern Performance Management: Beyond Annual Reviews',
      excerpt: 'Explore how continuous feedback and data-driven insights are reshaping performance management.',
      category: 'management',
      readTime: '10 min read',
      author: 'Emily Watson',
      date: 'Dec 10, 2024',
      image: 'https://images.unsplash.com/photo-1552664688-cf412ec27db2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&h=600&q=80',
      views: '3.1k',
      tags: ['Performance', 'Feedback', 'Analytics'],
      content: `The era of annual performance reviews is over. Modern organizations are adopting continuous performance management:

## Continuous Feedback Framework:
**Regular Check-ins:**
- Weekly one-on-one meetings
- Real-time feedback tools
- Peer recognition platforms

**Goal Management:**
- OKRs (Objectives and Key Results)
- SMART goals with regular updates
- Progress tracking and adjustments

## Technology Integration:
• AI-powered performance analytics
• 360-degree feedback systems
• Skill development tracking
• Career path planning tools

## Benefits:
- 30% increase in employee engagement
- 25% improvement in productivity
- Better retention of top performers
- Data-driven promotion decisions

Transform your performance management with continuous, meaningful conversations.`
    }
  ];

  // Blog posts data
  const blogPosts = [
    {
      id: 1,
      title: 'Essential Hiring Requirements for Modern Organizations',
      excerpt: 'Comprehensive guide to establishing effective hiring criteria that align with your company culture and goals.',
      content: `# Essential Hiring Requirements for Modern Organizations

In today's competitive job market, having clear hiring requirements is crucial for attracting the right talent.

## Key Hiring Requirements:

### 1. Role-Specific Competencies
- Technical skills and qualifications
- Soft skills and interpersonal abilities
- Industry-specific knowledge
- Problem-solving capabilities

### 2. Cultural Fit Assessment
- Alignment with company values
- Team collaboration style
- Adaptability to company culture
- Long-term growth potential

### 3. Legal Compliance
- Equal Employment Opportunity guidelines
- Background check regulations
- Interview question compliance
- Documentation requirements

### 4. AI-Powered Screening
- Resume parsing and matching
- Skill assessment automation
- Bias detection algorithms
- Candidate ranking systems

## Best Practices:
• Define clear job descriptions
• Use structured interviews
• Implement skill assessments
• Conduct thorough reference checks

Companies with well-defined hiring requirements see 50% better hiring outcomes.`,
      category: 'hiring',
      readTime: '7 min read',
      author: 'David Kim',
      authorRole: 'Head of Talent Acquisition',
      authorImage: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&h=100&q=80',
      date: 'Dec 14, 2024',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&h=600&q=80',
      views: '1.2k',
      likes: 89,
      tags: ['Hiring', 'Recruitment', 'Compliance'],
      hasForm: true
    },
    {
      id: 2,
      title: 'Streamlining Employee Onboarding with HR Copilot',
      excerpt: 'How to leverage AI-powered tools to create seamless and engaging onboarding experiences for new hires.',
      content: `# Streamlining Employee Onboarding with HR Copilot

Modern onboarding goes beyond paperwork. Learn how HR Copilot transforms the onboarding process.

## Automated Workflow Setup:
- Pre-boarding checklist automation
- Document collection and verification
- System access provisioning
- Equipment setup coordination

## Personalized Onboarding Paths:
- Role-specific training modules
- Department-specific introductions
- Custom learning pathways
- Mentor matching algorithms

## Digital Document Management:
- Electronic signature integration
- Document tracking and reminders
- Compliance requirement tracking
- Centralized document repository

## AI-Powered Features:
- Smart buddy matching
- Progress prediction analytics
- Engagement monitoring
- Automated follow-ups

## Integration Capabilities:
- HRIS system integration
- Learning management systems
- Communication platforms
- Performance management tools

Companies using automated onboarding see 70% faster time-to-productivity.`,
      category: 'onboarding',
      readTime: '5 min read',
      author: 'Lisa Wang',
      authorRole: 'Onboarding Specialist',
      authorImage: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&h=100&q=80',
      date: 'Dec 13, 2024',
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&h=600&q=80',
      views: '956',
      likes: 67,
      tags: ['Onboarding', 'Automation', 'AI'],
      hasForm: false
    },
    {
      id: 3,
      title: 'Effective Employee Management Strategies',
      excerpt: 'Best practices for managing teams, fostering growth, and maintaining high employee engagement.',
      content: `# Effective Employee Management Strategies

Successful employee management requires a balanced approach combining leadership, technology, and empathy.

## Key Management Strategies:

### 1. Regular One-on-One Meetings
- Weekly 30-minute sessions
- Structured agenda with employee input
- Action item tracking
- Progress review and feedback

### 2. Clear Goal Setting
- SMART goal framework
- Regular progress check-ins
- Alignment with company objectives
- Transparent performance metrics

### 3. Professional Development
- Individual development plans
- Skill gap analysis
- Training and certification support
- Career path planning

### 4. Recognition Systems
- Peer-to-peer recognition
- Performance-based rewards
- Milestone celebrations
- Public acknowledgment

### 5. Work-Life Balance
- Flexible work arrangements
- Mental health support
- Vacation encouragement
- Burnout prevention

Companies with effective management see 40% lower turnover rates.`,
      category: 'management',
      readTime: '9 min read',
      author: 'Robert Johnson',
      authorRole: 'HR Director',
      authorImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&h=100&q=80',
      date: 'Dec 11, 2024',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&h=600&q=80',
      views: '1.5k',
      likes: 112,
      tags: ['Management', 'Leadership', 'Engagement'],
      hasForm: false
    },
    {
      id: 4,
      title: 'Manager Development: Building Effective Leaders',
      excerpt: 'Essential training and development programs for managers to excel in their leadership roles.',
      content: `# Manager Development: Building Effective Leaders

Great managers are made, not born. Comprehensive development programs are essential for leadership success.

## Leadership Competency Framework:

### 1. Communication Skills
- Active listening techniques
- Constructive feedback delivery
- Conflict resolution strategies
- Transparent communication

### 2. Decision-Making Capabilities
- Data-driven decision making
- Risk assessment and management
- Stakeholder consideration
- Ethical decision frameworks

### 3. Team Building
- Talent development strategies
- Team motivation techniques
- Diversity and inclusion promotion
- Succession planning

### 4. Performance Coaching
- Coaching vs. managing distinction
- Development plan creation
- Performance improvement plans
- Career guidance

## Training Programs:
- Leadership workshops (quarterly)
- Mentorship programs
- External certification support
- Peer learning circles

Investing in manager development returns 5x in improved team performance.`,
      category: 'leadership',
      readTime: '8 min read',
      author: 'Maria Garcia',
      authorRole: 'Leadership Coach',
      authorImage: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&h=100&q=80',
      date: 'Dec 9, 2024',
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&h=600&q=80',
      views: '1.8k',
      likes: 134,
      tags: ['Leadership', 'Development', 'Training'],
      hasForm: false
    },
    {
      id: 5,
      title: 'Legal Compliance in Hiring Processes',
      excerpt: 'Understanding and implementing legally compliant hiring practices to protect your organization.',
      content: `# Legal Compliance in Hiring Processes

Stay compliant with evolving employment laws to protect your organization and ensure fair hiring practices.

## Key Compliance Areas:

### 1. Equal Employment Opportunity
- Protected category awareness
- Non-discriminatory job descriptions
- Fair interview practices
- Reasonable accommodation processes

### 2. Background Check Regulations
- FCRA compliance requirements
- State-specific regulations
- Consent and disclosure procedures
- Adverse action processes

### 3. Interview Compliance
- Legal vs. illegal questions
- Documentation requirements
- Consistency in questioning
- Bias prevention training

### 4. International Considerations
- Global hiring compliance
- Work visa requirements
- Local employment laws
- Cross-border data privacy

## Compliance Checklist:
- Regular policy reviews
- Manager training programs
- Documentation audits
- Legal counsel consultation

Proper compliance reduces legal risks by 85%.`,
      category: 'hiring',
      readTime: '6 min read',
      author: 'James Wilson',
      authorRole: 'Employment Lawyer',
      authorImage: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&h=100&q=80',
      date: 'Dec 8, 2024',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&h=600&q=80',
      views: '892',
      likes: 45,
      tags: ['Compliance', 'Legal', 'Hiring'],
      hasForm: true
    },
    {
      id: 6,
      title: 'Leveraging HR Analytics for Better Decisions',
      excerpt: 'How to use data and analytics to drive strategic HR decisions and improve organizational outcomes.',
      content: `# Leveraging HR Analytics for Better Decisions

Data-driven HR is transforming how organizations make people-related decisions and measure impact.

## Key Analytics Areas:

### 1. Talent Acquisition Analytics
- Time-to-fill metrics
- Source effectiveness analysis
- Quality of hire measurements
- Candidate experience scores

### 2. Employee Retention Predictors
- Turnover risk modeling
- Engagement correlation analysis
- Exit interview analytics
- Retention cost calculations

### 3. Performance Analytics
- Performance trend analysis
- High-potential identification
- Team performance metrics
- Promotion success rates

### 4. Diversity and Inclusion Metrics
- Representation analytics
- Pay equity analysis
- Promotion rate comparisons
- Inclusion survey results

## Implementation Strategy:
1. Data collection and cleaning
2. Dashboard development
3. Manager training
4. Action plan creation

Organizations using HR analytics see 25% better business outcomes.`,
      category: 'analytics',
      readTime: '11 min read',
      author: 'Dr. Amanda Chen',
      authorRole: 'HR Data Scientist',
      authorImage: 'https://images.unsplash.com/photo-1551836026-d5c88ac5c4e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&h=100&q=80',
      date: 'Dec 7, 2024',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&h=600&q=80',
      views: '2.3k',
      likes: 156,
      tags: ['Analytics', 'Data', 'Strategy'],
      hasForm: false
    }
  ];

  // Filter blog posts based on search, category, tags, and sort
  const filteredPosts = blogPosts
    .filter(post => {
      const matchesSearch = searchQuery === '' || 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())) ||
        post.author.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory = activeCategory === 'all' || post.category === activeCategory;
      
      const matchesTags = selectedTags.length === 0 || 
        selectedTags.every(tag => post.tags.includes(tag));

      return matchesSearch && matchesCategory && matchesTags;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.date) - new Date(a.date);
        case 'popular':
          return parseInt(b.views) - parseInt(a.views);
        case 'trending':
          return parseInt(b.likes) - parseInt(a.likes);
        default:
          return 0;
      }
    });

  // Update category counts based on filtered posts
  useEffect(() => {
    categories.forEach(cat => {
      if (cat.id !== 'all') {
        cat.count = blogPosts.filter(post => 
          post.category === cat.id && 
          (searchQuery === '' || 
           post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
           post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
           post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
        ).length;
      } else {
        cat.count = blogPosts.filter(post => 
          searchQuery === '' || 
          post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
        ).length;
      }
    });
  }, [searchQuery]);

  const nextFeaturedPost = () => {
    setFeaturedPost((prev) => (prev + 1) % featuredPosts.length);
  };

  const prevFeaturedPost = () => {
    setFeaturedPost((prev) => (prev - 1 + featuredPosts.length) % featuredPosts.length);
  };

  const handleReadArticle = (post) => {
    setSelectedPost(post);
  };

  const handleCloseArticle = () => {
    setSelectedPost(null);
  };

  const handleTagClick = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setActiveCategory('all');
    setSelectedTags([]);
    setSortBy('newest');
  };

  const HiringForm = ({ post }) => (
    <div className="blog__hiring-form">
      <h3>Request Hiring Consultation</h3>
      <p>Get expert guidance on implementing these hiring requirements in your organization.</p>
      
      <form className="blog__form">
        <div className="blog__form-group">
          <label>Full Name</label>
          <input type="text" placeholder="Enter your full name" />
        </div>
        
        <div className="blog__form-group">
          <label>Email Address</label>
          <input type="email" placeholder="Enter your work email" />
        </div>
        
        <div className="blog__form-group">
          <label>Company</label>
          <input type="text" placeholder="Enter company name" />
        </div>
        
        <div className="blog__form-group">
          <label>Team Size</label>
          <select>
            <option value="">Select team size</option>
            <option value="1-10">1-10 employees</option>
            <option value="11-50">11-50 employees</option>
            <option value="51-200">51-200 employees</option>
            <option value="201-500">201-500 employees</option>
            <option value="501+">501+ employees</option>
          </select>
        </div>
        
        <div className="blog__form-group">
          <label>Specific Hiring Challenges</label>
          <textarea 
            placeholder="Describe your current hiring challenges..." 
            rows="4"
          ></textarea>
        </div>
        
        <button type="submit" className="blog__form-submit">
          Request Consultation
        </button>
      </form>
    </div>
  );

  const ArticleModal = ({ post, onClose }) => (
    <div className="blog__modal-overlay" onClick={onClose}>
      <div className="blog__modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="blog__modal-close" onClick={onClose}>
          <FaTimes />
        </button>
        
        <div className="blog__modal-header">
          <img src={post.image} alt={post.title} className="blog__modal-image" />
          <div className="blog__modal-meta">
            <span className="blog__modal-category">{post.category}</span>
            <h2 className="blog__modal-title">{post.title}</h2>
            <div className="blog__modal-author">
              <img src={post.authorImage} alt={post.author} />
              <div>
                <span className="blog__author-name">{post.author}</span>
                <span className="blog__author-role">{post.authorRole}</span>
              </div>
            </div>
            <div className="blog__modal-stats">
              <span><FaClock /> {post.readTime}</span>
              <span><FaEye /> {post.views} views</span>
              <span><FaCalendarAlt /> {post.date}</span>
            </div>
          </div>
        </div>
        
        <div className="blog__modal-body">
          <div className="blog__article-content">
            {post.content.split('\n').map((paragraph, index) => {
              if (paragraph.startsWith('## ')) {
                return <h3 key={index}>{paragraph.replace('## ', '')}</h3>;
              } else if (paragraph.startsWith('### ')) {
                return <h4 key={index}>{paragraph.replace('### ', '')}</h4>;
              } else if (paragraph.startsWith('- ') || paragraph.startsWith('• ')) {
                return <li key={index}>{paragraph.replace(/^[-•] /, '')}</li>;
              } else if (paragraph.trim() === '') {
                return <br key={index} />;
              } else {
                return <p key={index}>{paragraph}</p>;
              }
            })}
          </div>
          
          {post.hasForm && <HiringForm post={post} />}
        </div>
        
        <div className="blog__modal-footer">
          <div className="blog__modal-tags">
            {post.tags.map(tag => (
              <span key={tag} className="blog__post-tag">{tag}</span>
            ))}
          </div>
          <div className="blog__modal-actions">
            <button className="blog__action-btn">
              <FaBookmark /> Save
            </button>
            <button className="blog__action-btn">
              <FaShare /> Share
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="blog">
      {/* Article Modal */}
      {selectedPost && (
        <ArticleModal post={selectedPost} onClose={handleCloseArticle} />
      )}

      {/* Hero Section */}
      <section className="blog__hero">
        <div className="blog__container">
          <div className="blog__hero-content">
            <h1 className="blog__title">HR Copilot Blog</h1>
            <p className="blog__subtitle">
              Insights, strategies, and best practices for modern HR professionals. 
              Stay updated with the latest trends in hiring, onboarding, employee management, and leadership.
            </p>
            
            {/* Search Bar */}
            <div className="blog__search">
              <div className="blog__search-container">
                <FaSearch className="blog__search-icon" />
                <input
                  type="text"
                  placeholder="Search articles, topics, or authors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="blog__search-input"
                />
                <button 
                  className="blog__search-btn"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <FaFilter />
                  Filters
                </button>
              </div>

              {/* Active Filters Display */}
              {(searchQuery || activeCategory !== 'all' || selectedTags.length > 0) && (
                <div className="blog__active-filters">
                  <span>Active filters:</span>
                  {searchQuery && (
                    <span className="blog__active-filter">
                      Search: "{searchQuery}"
                      <button onClick={() => setSearchQuery('')}>×</button>
                    </span>
                  )}
                  {activeCategory !== 'all' && (
                    <span className="blog__active-filter">
                      Category: {categories.find(cat => cat.id === activeCategory)?.name}
                      <button onClick={() => setActiveCategory('all')}>×</button>
                    </span>
                  )}
                  {selectedTags.map(tag => (
                    <span key={tag} className="blog__active-filter">
                      Tag: {tag}
                      <button onClick={() => handleTagClick(tag)}>×</button>
                    </span>
                  ))}
                  <button className="blog__clear-filters" onClick={clearAllFilters}>
                    Clear All
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Posts Carousel */}
      <section className="blog__featured">
        <div className="blog__container">
          <div className="blog__section-header">
            <h2 className="blog__section-title">Featured Articles</h2>
            <div className="blog__carousel-controls">
              <button onClick={prevFeaturedPost} className="blog__carousel-btn">
                <FaChevronRight style={{ transform: 'rotate(180deg)' }} />
              </button>
              <button onClick={nextFeaturedPost} className="blog__carousel-btn">
                <FaChevronRight />
              </button>
            </div>
          </div>

          <div className="blog__featured-carousel">
            <div 
              className="blog__featured-post"
              style={{ backgroundImage: `url(${featuredPosts[featuredPost].image})` }}
            >
              <div className="blog__featured-overlay">
                <div className="blog__featured-content">
                  <span className="blog__featured-category">
                    {featuredPosts[featuredPost].category}
                  </span>
                  <h3 className="blog__featured-title">
                    {featuredPosts[featuredPost].title}
                  </h3>
                  <p className="blog__featured-excerpt">
                    {featuredPosts[featuredPost].excerpt}
                  </p>
                  <div className="blog__featured-meta">
                    <div className="blog__meta-item">
                      <FaClock />
                      <span>{featuredPosts[featuredPost].readTime}</span>
                    </div>
                    <div className="blog__meta-item">
                      <FaEye />
                      <span>{featuredPosts[featuredPost].views} views</span>
                    </div>
                    <div className="blog__meta-item">
                      <FaCalendarAlt />
                      <span>{featuredPosts[featuredPost].date}</span>
                    </div>
                  </div>
                  <div className="blog__featured-actions">
                    <button 
                      className="blog__read-btn"
                      onClick={() => handleReadArticle(featuredPosts[featuredPost])}
                    >
                      Read Article <FaArrowRight />
                    </button>
                    <div className="blog__action-buttons">
                      <button className="blog__action-btn">
                        <FaBookmark />
                      </button>
                      <button className="blog__action-btn">
                        <FaShare />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="blog__carousel-indicators">
            {featuredPosts.map((_, index) => (
              <button
                key={index}
                className={`blog__indicator ${index === featuredPost ? 'blog__indicator--active' : ''}`}
                onClick={() => setFeaturedPost(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="blog__main">
        <div className="blog__container">
          <div className="blog__layout">
            {/* Sidebar - Categories & Filters */}
            <aside className="blog__sidebar">
              <div className="blog__sidebar-section">
                <h3 className="blog__sidebar-title">Categories</h3>
                <div className="blog__categories">
                  {categories.map(category => (
                    <button
                      key={category.id}
                      className={`blog__category ${activeCategory === category.id ? 'blog__category--active' : ''}`}
                      onClick={() => setActiveCategory(category.id)}
                    >
                      <div className="blog__category-content">
                        <div className="blog__category-icon">
                          {category.icon}
                        </div>
                        <span className="blog__category-name">
                          {category.name}
                        </span>
                      </div>
                      <span className="blog__category-count">
                        {category.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Popular Tags */}
              <div className="blog__sidebar-section">
                <h3 className="blog__sidebar-title">Popular Tags</h3>
                <div className="blog__tags">
                  {allTags.map(tag => (
                    <span 
                      key={tag} 
                      className={`blog__tag ${selectedTags.includes(tag) ? 'blog__tag--active' : ''}`}
                      onClick={() => handleTagClick(tag)}
                    >
                      <FaTag />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Sort Options */}
              <div className="blog__sidebar-section">
                <h3 className="blog__sidebar-title">Sort By</h3>
                <div className="blog__sort-options">
                  <label className="blog__sort-option">
                    <input 
                      type="radio" 
                      name="sort" 
                      value="newest" 
                      checked={sortBy === 'newest'}
                      onChange={(e) => setSortBy(e.target.value)}
                    />
                    <span>Newest First</span>
                  </label>
                  <label className="blog__sort-option">
                    <input 
                      type="radio" 
                      name="sort" 
                      value="popular" 
                      checked={sortBy === 'popular'}
                      onChange={(e) => setSortBy(e.target.value)}
                    />
                    <span>Most Popular</span>
                  </label>
                  <label className="blog__sort-option">
                    <input 
                      type="radio" 
                      name="sort" 
                      value="trending" 
                      checked={sortBy === 'trending'}
                      onChange={(e) => setSortBy(e.target.value)}
                    />
                    <span>Trending</span>
                  </label>
                </div>
              </div>

              {/* Newsletter */}
              <div className="blog__newsletter">
                <h4>Stay Updated</h4>
                <p>Get the latest HR insights delivered to your inbox</p>
                <div className="blog__newsletter-form">
                  <input type="email" placeholder="Enter your email" />
                </div>
              </div>
            </aside>

            {/* Blog Posts Grid */}
            <main className="blog__content">
              <div className="blog__posts-header">
                <h2 className="blog__section-title">
                  {activeCategory === 'all' ? 'All Articles' : 
                   categories.find(cat => cat.id === activeCategory)?.name}
                  <span className="blog__posts-count">({filteredPosts.length})</span>
                </h2>
                <div className="blog__sort">
                  <select 
                    className="blog__sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="newest">Newest First</option>
                    <option value="popular">Most Popular</option>
                    <option value="trending">Trending</option>
                  </select>
                </div>
              </div>

              <div className="blog__posts-grid">
                {filteredPosts.map(post => (
                  <article key={post.id} className="blog__post-card">
                    <div className="blog__post-image">
                      <img src={post.image} alt={post.title} />
                      <span className="blog__post-category">
                        {post.category}
                      </span>
                    </div>
                    
                    <div className="blog__post-content">
                      <div className="blog__post-meta">
                        <div className="blog__author">
                          <img src={post.authorImage} alt={post.author} />
                          <div>
                            <span className="blog__author-name">{post.author}</span>
                            <span className="blog__author-role">{post.authorRole}</span>
                          </div>
                        </div>
                        <div className="blog__post-date">
                          <FaCalendarAlt />
                          {post.date}
                        </div>
                      </div>

                      <h3 className="blog__post-title">{post.title}</h3>
                      <p className="blog__post-excerpt">{post.excerpt}</p>

                      <div className="blog__post-footer">
                        <div className="blog__post-stats">
                          <div className="blog__stat">
                            <FaClock />
                            <span>{post.readTime}</span>
                          </div>
                          <div className="blog__stat">
                            <FaEye />
                            <span>{post.views}</span>
                          </div>
                          <div className="blog__stat">
                            <FaComments />
                            <span>{post.likes}</span>
                          </div>
                        </div>

                        <div className="blog__post-actions">
                          <button className="blog__action-btn">
                            <FaBookmark />
                          </button>
                          <button className="blog__action-btn">
                            <FaShare />
                          </button>
                          <button 
                            className="blog__read-more"
                            onClick={() => handleReadArticle(post)}
                          >
                            Read <FaArrowRight />
                          </button>
                        </div>
                      </div>

                      <div className="blog__post-tags">
                        {post.tags.map(tag => (
                          <span key={tag} className="blog__post-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {filteredPosts.length === 0 && (
                <div className="blog__no-results">
                  <h3>No articles found</h3>
                  <p>Try adjusting your search terms or browse different categories.</p>
                  <button className="blog__clear-filters" onClick={clearAllFilters}>
                    Clear All Filters
                  </button>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      {/* CTA Section */}
  
    </div>
  );
};

export default Blog;